/**
 * Interface pour les résultats du parsing d'URL
 */
export interface ParsedUrl {
  base_url: string;
  base_route: string;
  params: Record<string, string | number>;
}

/**
 * Interface pour les résultats du matching de pattern
 */
export interface PatternMatchResult {
  match: boolean;
  params: Record<string, string | number>;
}

/**
 * Helper pour parser une URL et extraire les composants
 *
 * @param url - L'URL complète à parser
 * @param baseUrl - L'URL de base (optionnel, sera déduit de l'URL si non fourni)
 * @returns ParsedUrl contenant base_url, base_route et params
 *
 * @example
 * ```typescript
 * const result = parseUrl(
 *   'http://localhost:8000/users/100?page=0&pageSize=10&sortField=lastname&sortOrder=desc',
 *   'http://localhost:8000/'
 * );
 *
 * // result = {
 * //   base_url: 'http://localhost:8000/',
 * //   base_route: '/users/100',
 * //   params: {
 * //     page: 0,
 * //     pageSize: 10,
 * //     sortField: 'lastname',
 * //     sortOrder: 'desc'
 * //   }
 * // }
 * ```
 */
export function parseUrl(url: string, baseUrl?: string): ParsedUrl {
  try {
    // Gérer les URLs relatives (assets, fichiers locaux)
    if (url.startsWith('../') || url.startsWith('./') || url.startsWith('assets/') || !url.includes('://')) {
      return {
        base_url: '',
        base_route: url,
        params: {},
      };
    }

    const urlObj = new URL(url);

    // Déterminer base_url
    let resolvedBaseUrl: string;
    if (baseUrl) {
      // S'assurer que baseUrl se termine par '/'
      resolvedBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    } else {
      // Construire base_url à partir de l'URL
      resolvedBaseUrl = `${urlObj.protocol}//${urlObj.host}/`;
    }

    // Extraire base_route (pathname sans les query params et sans le / initial)
    const baseRoute = urlObj.pathname.startsWith('/') ? urlObj.pathname.slice(1) : urlObj.pathname;

    // Parser les query parameters
    const params: Record<string, string | number> = {};
    urlObj.searchParams.forEach((value, key) => {
      // Essayer de convertir en nombre si possible
      const numValue = Number(value);
      if (!isNaN(numValue) && value.trim() !== '') {
        params[key] = numValue;
      } else {
        params[key] = value;
      }
    });

    return {
      base_url: resolvedBaseUrl,
      base_route: baseRoute,
      params,
    };
  } catch (error) {
    throw new Error(`Erreur lors du parsing de l'URL: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
}

/**
 * Helper pour reconstruire une URL à partir des composants parsés
 *
 * @param parsedUrl - L'objet ParsedUrl
 * @returns L'URL complète reconstituée
 *
 * @example
 * ```typescript
 * const rebuilt = buildUrl({
 *   base_url: 'http://localhost:8000/',
 *   base_route: '/users/100',
 *   params: { page: 0, pageSize: 10 }
 * });
 * // rebuilt = 'http://localhost:8000/users/100?page=0&pageSize=10'
 * ```
 */
export function buildUrl(parsedUrl: ParsedUrl): string {
  const { base_url, base_route, params } = parsedUrl;

  // Construire l'URL de base
  const baseUrlClean = base_url.endsWith('/') ? base_url.slice(0, -1) : base_url;
  const routeClean = base_route.startsWith('/') ? base_route : '/' + base_route;

  let url = baseUrlClean + routeClean;

  // Ajouter les paramètres si ils existent
  const paramEntries = Object.entries(params);
  if (paramEntries.length > 0) {
    const queryString = paramEntries
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`)
      .join('&');
    url += '?' + queryString;
  }

  return url;
}

/**
 * Helper pour extraire seulement les paramètres d'une URL
 *
 * @param url - L'URL à parser
 * @returns Record contenant les paramètres
 */
export function extractUrlParams(url: string): Record<string, string | number> {
  return parseUrl(url).params;
}

/**
 * Helper pour extraire seulement la route d'une URL
 *
 * @param url - L'URL à parser
 * @param baseUrl - L'URL de base (optionnel)
 * @returns La route sans les paramètres
 */
export function extractBaseRoute(url: string, baseUrl?: string): string {
  return parseUrl(url, baseUrl).base_route;
}

/**
 * Helper pour vérifier si une URL matchée correspond à un pattern avec des paramètres dynamiques
 *
 * @param parsedUrl - L'objet ParsedUrl résultant de parseUrl()
 * @param pattern - Le pattern à matcher (ex: '/mobile/{directory}/{filename}')
 * @returns PatternMatchResult contenant match (boolean) et params (paramètres extraits)
 *
 * @example
 * ```typescript
 * const parsedUrl = parseUrl('http://localhost:8000/mobile/dashboard/test.pdf');
 * const result = matchPattern(parsedUrl, '/mobile/{directory}/{filename}');
 * // result = {
 * //   match: true,
 * //   params: { directory: 'dashboard', filename: 'test.pdf' }
 * // }
 *
 * const parsedUrl2 = parseUrl('http://localhost:8000/update/201/zouma');
 * const result2 = matchPattern(parsedUrl2, '/update/{id}/zouma');
 * // result2 = {
 * //   match: true,
 * //   params: { id: 201 }
 * // }
 *
 * const parsedUrl3 = parseUrl('http://localhost:8000/user/45/posts/67');
 * const result3 = matchPattern(parsedUrl3, '/user/{userId}/posts/{postId}');
 * // result3 = {
 * //   match: true,
 * //   params: { userId: 45, postId: 67 }
 * // }
 *
 * const parsedUrl4 = parseUrl('http://localhost:8000/users/all');
 * const result4 = matchPattern(parsedUrl4, '/users/all');
 * // result4 = {
 * //   match: true,
 * //   params: {}
 * // }
 * ```
 */
export function matchPattern(parsedUrl: ParsedUrl, pattern: string): PatternMatchResult {
  // Normaliser la route (ajouter / au début si absent)
  const route = parsedUrl.base_route.startsWith('/') ? parsedUrl.base_route : '/' + parsedUrl.base_route;

  // Normaliser le pattern (ajouter / au début si absent)
  const normalizedPattern = pattern.startsWith('/') ? pattern : '/' + pattern;

  // Extraire les noms des paramètres du pattern
  const paramNames: string[] = [];
  const regexPattern = normalizedPattern.replace(/\{([^}]+)\}/g, (_, paramName) => {
    paramNames.push(paramName);
    return '([^/]+)'; // Capturer n'importe quel caractère sauf /
  });

  // Créer une regex pour matcher la route
  const regex = new RegExp(`^${regexPattern}$`);
  const match = route.match(regex);

  if (!match) {
    return {
      match: false,
      params: {},
    };
  }

  // Extraire les valeurs des paramètres
  const params: Record<string, string | number> = {};
  paramNames.forEach((paramName, index) => {
    const value = match[index + 1];
    // Essayer de convertir en nombre si possible
    const numValue = Number(value);
    if (!isNaN(numValue) && value.trim() !== '') {
      params[paramName] = numValue;
    } else {
      params[paramName] = value;
    }
  });

  return {
    match: true,
    params,
  };
}
